import * as Sentry from "@sentry/react";
import { browserTracingIntegration, makeFetchTransport, replayIntegration } from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./i18n";
// Fonts are served from this site, not Google Fonts: Google received every
// visitor's IP address on every page. Same faces as before (DM Sans with its
// optical-size axis, DM Serif Display), Latin and Latin Extended subsets.
import "@fontsource-variable/dm-sans/opsz.css";
import "@fontsource-variable/dm-sans/opsz-italic.css";
import "@fontsource/dm-serif-display/400.css";
import "@fontsource/dm-serif-display/400-italic.css";
import "./index.css";
import "leaflet/dist/leaflet.css";
import { setRequestHeadersGetter } from "@workspace/api-client-react";
import { WEB_CLIENT_HEADERS } from "@/lib/clientHeaders";
import { takeResetTokenFromAddress } from "@/lib/resetToken";
import { scrubbingTransport } from "@/lib/sentryScrub";
import { isChunkLoadError, reloadOnceForStaleChunk } from "@/lib/staleChunk";

// Round 29: name the website on every generated-client request (listing
// create and edit go through it).
setRequestHeadersGetter(() => ({ ...WEB_CLIENT_HEADERS }));

// The reset token leaves the address bar before Sentry starts recording it.
takeResetTokenFromAddress();

// Every envelope is scrubbed of URL query strings and fragments (reset token,
// search text) in the transport; see lib/sentryScrub.ts. Replay compression
// is off so the recording can be scrubbed too. Replay's defaults mask all
// text and inputs and block media; they are restated so they cannot be lost.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  transport: scrubbingTransport(makeFetchTransport),
  integrations: [
    browserTracingIntegration(),
    replayIntegration({ useCompression: false, maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
  ],
  tracesSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  sendDefaultPii: false,
});

// Route pages recover from a stale chunk after a deploy before the error
// reaches here (lib/staleChunk.ts, used by App.tsx). This is the backstop for
// any other dynamic import, under the same once-per-window guard.
function ErrorFallback({ error }: { error: unknown }) {
  if (isChunkLoadError(error) && reloadOnceForStaleChunk()) return null;
  return <p>Something went wrong. Please reload the page.</p>;
}

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={({ error }) => <ErrorFallback error={error} />}>
    <App />
  </Sentry.ErrorBoundary>
);
